import BaseScenario from './Scenario/BaseScenario';
import { Environment } from './Environment';
import Store from './Store';
import UIManager from './UI/UiManager';
import SoundManager from './Sound/SoundManager';

/**
 * Manages the game
 */
export default class GameManager {
	/**
	 * A reference to the UI Manager
	 */
	public UiManager: UIManager | null = null;

	/**
	 * A reference to the Sound Manager
	 */
	public SoundManager: SoundManager | null = null;
	
	/**
	 * A reference to all scenarios
	 */
	public scenarios: BaseScenario[] | null = null;
	
	/**
	 * A reference to the environment to use
	 */
	public environment: Environment<any> | null = null;
	
	/**
	 * Information for the user
	 */
	public userInfo: any;
	
	/**
	 * A reference to the store
	 */
	public store: Store | null = null;
	
	/**
	 * To keep track of any custom data
	 */
	public customData: any;

	public get currentScenarioIndex(): number {
		return this._currentScenarioIndex;
	}
	
	private _currentScenarioIndex: number = -1;
	
	/**
	 * ctor
	 */
	constructor() { 
    	this.customData = null;
	}

	/**
	 * Intializes the manager
	 * @param scenarios All scenarios to use
	 * @param environment  The environment to use
	 * @param uiManager The UI manager to use
	 * @param soundManager  The sound manager to use
	 * @param loadLatestScenario True if we want to load the latest scenario that was saved
	 * @param firstScenarioName The name of the first scenario
	 * @param firstScenarioProps The properties for the first scenario
	 */
	init(scenarios: BaseScenario[], environment: Environment<any>, uiManager: UIManager, soundManager: SoundManager, loadLatestScenario: boolean, firstScenarioName: string, firstScenarioProps: any, onForceUpdate: () => void) {
		if(!scenarios || scenarios.length === 0) {
    		throw new Error ('There must be at least 1 scenario');
    	}
        
    	if(!environment || !environment.playSequence || !environment.render) {
    		throw new Error('This is not a valid environment. Be sure to extend from Environment');
    	}
    	this.scenarios = scenarios;
    	this.UiManager = uiManager;
    	this.environment = environment;
		this.SoundManager = soundManager;
    	this.store = new Store();

    	if(loadLatestScenario) {
    		var rawData: string = this.store.get('game');
    		var didLoadSaveGame = typeof rawData === 'string';
    
    		if(didLoadSaveGame) {
    			var gameData = JSON.parse(rawData);
    			this.UiManager = gameData.uiManager;
    			this.scenarios = gameData.scenarios;
    			this._currentScenarioIndex = gameData.currentScenarioIndex;
    			this.environment = gameData.environment;
    			this.userInfo = gameData.userInfo;
    			this.loadScenario(firstScenarioName, firstScenarioProps);
    		} else {
    			if(firstScenarioName) {
    				this.loadScenario(firstScenarioName, firstScenarioProps);
    			}
    		}
    	} else {
    		if(firstScenarioName) {
    			this.loadScenario(firstScenarioName, firstScenarioProps);
    		}
    	}
	}

	/**
	 * Logs a message
	 */
	public log(msg: string): void {
    	console.log(msg);
	}

	/**
	 * Saves the current state of the game
	 */
	public save(): void {
    	if(!this.store) {
    		return;
    	}

    	this.store.set('game', {
    		uiManager: this.UiManager,
    		scenarios: this.scenarios,
    		currentScenarioIndex: this._currentScenarioIndex,
    		environment: this.environment,
    		userInfo: this.userInfo
    	});
	}

	/**
	 * True if there is any save data
	 */
	public hasSaveData(): boolean {
    	if(!this.store) {
    		return false;
    	}

    	var data = this.store.get('game');
    	return data !== null && typeof data === 'string';
	}

	/**
	 * Loads a scenario
	 * @param scenarioName The name of the scenario
	 * @param scenarioProps The properties for that scenario
	 */
	public loadScenario(scenarioName: string, scenarioProps: any): void {
    	var newScenarioIndex = -1;
    	if(!scenarioProps) {
    		scenarioProps = {};
    	}

    	if(!this.scenarios) {
    		return;
    	}

    	for(var indx = 0; indx < this.scenarios.length; indx++) {
    		if(this.scenarios[indx].props.name === scenarioName) {
    			newScenarioIndex = indx;
    			break;
    		}
    	}

    	if(newScenarioIndex !== -1) {
    		this.endScenario();
    		this._currentScenarioIndex = newScenarioIndex;
    		scenarioProps.name = this.scenarios[this._currentScenarioIndex].props.name;
    		this.scenarios[this._currentScenarioIndex].props = scenarioProps;
    		this.scenarios[this._currentScenarioIndex].startWhenReady();
    	} else {
    		throw new Error('could not find scenario with name ' + scenarioName);
    	}
	}

	/**
	 * Ends a scenario
	 */
	public endScenario(): void {
    	if(!this.scenarios) {
    		return;
    	}

    	if(this._currentScenarioIndex !== -1) {
    		this.scenarios[this._currentScenarioIndex].endScenario();
    		this._currentScenarioIndex = -1;
    	}
	}
}